import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InventoryGeneralFlatTeamSettingGroupId =
  | "inventory_general.reserved_inventory_buckets"
  | "inventory_general.adjustment_reasons"
  | "inventory_general.product_merging";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.ims",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason: "Inventory defaults for reserved stock and adjustment reasons.",
};

export const InventoryGeneralFlatTeamSettingGroups = [
  {
    id: "inventory_general.reserved_inventory_buckets",
    title: "Reserved inventory buckets",
    description:
      "The set of reserved inventory buckets your team can assign stock to, so reserved units are excluded from sellable inventory. Use this to create, rename, and remove the buckets themselves.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "inventory-management/general" },
    synonyms: ["reserved inventory buckets", "inventory general"],
  },
  {
    id: "inventory_general.adjustment_reasons",
    title: "Adjustment reasons",
    description:
      "The reasons staff choose when adjusting inventory, such as damage, shrinkage, or write-offs. Add, rename, or remove the reasons offered in the adjustment flow.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "inventory-management/general" },
    synonyms: ["adjustment reasons", "inventory general"],
  },
  {
    id: "inventory_general.product_merging",
    title: "Product merging",
    description:
      "Whether products that arrive from different sales channels sharing a SKU are automatically merged into one canonical product. Turn this off to keep every synced product as its own record.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "inventory-management/general" },
    synonyms: [
      "product merging",
      "merge duplicate products",
      "inventory general",
    ],
  },
] as const satisfies readonly SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>[];

type _assertAllInventoryGeneralFlatTeamSettingGroupIdsProvided =
  InventoryGeneralFlatTeamSettingGroupId extends (typeof InventoryGeneralFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allInventoryGeneralFlatTeamSettingGroupIdsProvided: _assertAllInventoryGeneralFlatTeamSettingGroupIdsProvided = true;
void _allInventoryGeneralFlatTeamSettingGroupIdsProvided;

export const InventoryGeneralFlatTeamSettingGroupById: Record<
  InventoryGeneralFlatTeamSettingGroupId,
  SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      InventoryGeneralFlatTeamSettingGroupId,
      SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of InventoryGeneralFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    InventoryGeneralFlatTeamSettingGroupId,
    SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>
  >;
})();
